import { useEffect, useRef, useState } from "react";
import { ConnectorCard } from "./ConnectorCard";
import {
  getConnectors,
  disconnectConnector,
  getConnectUrl,
  connectWithApiKey,
  connectTwitterWithCredentials,
  connectBlueBubbles,
} from "../../../services/crmConnectors";
import { ApiError } from "../../../services/api";
import type { ConnectorResponse } from "../../../types/crm";
import "./ConnectorSettingsPage.css";

// Quo is being phased out — BlueBubbles handles phone outreach now. New users
// should never see Quo as a connect option. Existing Quo connectors still
// render (computed in `platforms` below) so users can disconnect them.
const BASE_PLATFORMS = ["Slack", "Gmail", "Twitter", "BlueBubbles"] as const;
const BLUEBUBBLES_TUNNEL_DOC_URL =
  "https://github.com/seven-hills-technology/rosterpilot/blob/main/docs/integrations/bluebubbles-tunnel.md";
type BlueBubblesStep = "idle" | "reaching" | "verifying" | "registering";

export const ConnectorSettingsPage = () => {
  const [connectors, setConnectors] = useState<ConnectorResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [quoKeyInput, setQuoKeyInput] = useState("");
  const [showQuoKeyForm, setShowQuoKeyForm] = useState(false);
  const quoInputRef = useRef<HTMLInputElement>(null);
  const [showTwitterForm, setShowTwitterForm] = useState(false);
  const [twitterCredentials, setTwitterCredentials] = useState({
    consumerKey: "",
    consumerSecret: "",
    accessToken: "",
    accessTokenSecret: "",
  });
  const twitterFirstInputRef = useRef<HTMLInputElement>(null);
  const [showBlueBubblesForm, setShowBlueBubblesForm] = useState(false);
  const [bbServerUrl, setBbServerUrl] = useState("");
  const [bbPassword, setBbPassword] = useState("");
  const [bbConsentChecked, setBbConsentChecked] = useState(false);
  const [bbFormError, setBbFormError] = useState("");
  const [bbStep, setBbStep] = useState<BlueBubblesStep>("idle");
  const bbServerUrlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConnectors();
  }, []);

  useEffect(() => {
    if (showQuoKeyForm) quoInputRef.current?.focus();
  }, [showQuoKeyForm]);

  useEffect(() => {
    if (showTwitterForm) twitterFirstInputRef.current?.focus();
  }, [showTwitterForm]);

  useEffect(() => {
    if (showBlueBubblesForm) bbServerUrlRef.current?.focus();
  }, [showBlueBubblesForm]);

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const data = await getConnectors();
      setConnectors(data);
    } catch {
      setError("Failed to load connectors.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (id: string) => {
    setDisconnecting(id);
    try {
      await disconnectConnector(id);
      setConnectors((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError("Failed to disconnect. Please try again.");
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnect = async (platform: string) => {
    if (platform === "Quo") {
      setShowQuoKeyForm(true);
      return;
    }
    if (platform === "Twitter") {
      setShowTwitterForm(true);
      return;
    }
    if (platform === "BlueBubbles") {
      setShowBlueBubblesForm(true);
      setBbFormError("");
      return;
    }
    setConnecting(platform);
    setError("");
    try {
      const { url } = await getConnectUrl(platform);
      window.location.href = url;
    } catch {
      setError(`Failed to start ${platform} connection. Please try again.`);
      setConnecting(null);
    }
  };

  const handleQuoKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quoKeyInput.trim()) return;
    setConnecting("Quo");
    setError("");
    try {
      await connectWithApiKey("Quo", quoKeyInput.trim());
      setShowQuoKeyForm(false);
      setQuoKeyInput("");
      await loadConnectors();
    } catch {
      setError("Failed to connect Quo. Check your API key and try again.");
    } finally {
      setConnecting(null);
    }
  };

  const handleTwitterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { consumerKey, consumerSecret, accessToken, accessTokenSecret } = twitterCredentials;
    if (!consumerKey.trim() || !consumerSecret.trim() || !accessToken.trim() || !accessTokenSecret.trim()) return;
    setConnecting("Twitter");
    setError("");
    try {
      await connectTwitterWithCredentials({
        consumerKey: consumerKey.trim(),
        consumerSecret: consumerSecret.trim(),
        accessToken: accessToken.trim(),
        accessTokenSecret: accessTokenSecret.trim(),
      });
      setShowTwitterForm(false);
      setTwitterCredentials({ consumerKey: "", consumerSecret: "", accessToken: "", accessTokenSecret: "" });
      await loadConnectors();
    } catch {
      setError("Failed to connect Twitter. Check your credentials and try again.");
    } finally {
      setConnecting(null);
    }
  };

  const updateTwitterField = (field: string, value: string) => {
    setTwitterCredentials((prev) => ({ ...prev, [field]: value }));
  };

  const resetBlueBubblesForm = () => {
    setShowBlueBubblesForm(false);
    setBbServerUrl("");
    setBbPassword("");
    setBbConsentChecked(false);
    setBbFormError("");
    setBbStep("idle");
  };

  const handleBlueBubblesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bbServerUrl.trim() || !bbPassword.trim() || !bbConsentChecked) return;

    setConnecting("BlueBubbles");
    setBbFormError("");
    setBbStep("reaching");

    // The API does test-connection → version-check → register-webhook serially. The frontend
    // can't observe each phase, so we advance the indicator on rough timers to reassure the
    // user during the wait. Cleared in finally regardless of outcome.
    const verifyingTimer = setTimeout(() => setBbStep("verifying"), 1500);
    const registeringTimer = setTimeout(() => setBbStep("registering"), 4000);

    try {
      await connectBlueBubbles(bbServerUrl.trim(), bbPassword);
      resetBlueBubblesForm();
      await loadConnectors();
    } catch (err) {
      const message =
        err instanceof ApiError && err.message
          ? err.message
          : "Failed to connect BlueBubbles. Please try again.";
      setBbFormError(message);
    } finally {
      clearTimeout(verifyingTimer);
      clearTimeout(registeringTimer);
      setBbStep("idle");
      setConnecting(null);
    }
  };

  const blueBubblesButtonLabel = (() => {
    switch (bbStep) {
      case "reaching":
        return "Reaching your server…";
      case "verifying":
        return "Verifying password…";
      case "registering":
        return "Registering webhook…";
      default:
        return "Connect";
    }
  })();

  return (
    <div className="connector-settings">
      <div className="connector-settings__header">
        <h2 className="connector-settings__title">Communication Connectors</h2>
        <p className="connector-settings__subtitle">
          Connect your communication platforms to automatically ingest messages.
        </p>
      </div>

      {error && <div className="connector-settings__error">{error}</div>}

      {loading ? (
        <div className="connector-settings__loading">Loading connectors...</div>
      ) : (
        <div className="connector-settings__list">
          {[
            ...BASE_PLATFORMS,
            ...(connectors.some((c) => c.connectorType === "Quo") ? ["Quo"] : []),
          ].map((platform) => {
            const connector = connectors.find((c) => c.connectorType === platform);
            return (
              <div key={platform}>
                <ConnectorCard
                  platform={platform}
                  connector={connector}
                  onConnect={handleConnect}
                  connecting={connecting === platform}
                  onDisconnect={handleDisconnect}
                  disconnecting={disconnecting === connector?.id}
                />
                {platform === "Twitter" && showTwitterForm && !connector && (
                  <form className="connector-settings__key-form" onSubmit={handleTwitterSubmit}>
                    <input
                      ref={twitterFirstInputRef}
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="Consumer Key (API Key)"
                      value={twitterCredentials.consumerKey}
                      onChange={(e) => updateTwitterField("consumerKey", e.target.value)}
                    />
                    <input
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="Consumer Secret (API Secret)"
                      value={twitterCredentials.consumerSecret}
                      onChange={(e) => updateTwitterField("consumerSecret", e.target.value)}
                    />
                    <input
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="Access Token"
                      value={twitterCredentials.accessToken}
                      onChange={(e) => updateTwitterField("accessToken", e.target.value)}
                    />
                    <input
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="Access Token Secret"
                      value={twitterCredentials.accessTokenSecret}
                      onChange={(e) => updateTwitterField("accessTokenSecret", e.target.value)}
                    />
                    <div className="connector-settings__key-form-actions">
                      <button
                        type="submit"
                        className="connector-settings__key-btn connector-settings__key-btn--save"
                        disabled={connecting === "Twitter" || !twitterCredentials.consumerKey.trim() || !twitterCredentials.consumerSecret.trim() || !twitterCredentials.accessToken.trim() || !twitterCredentials.accessTokenSecret.trim()}
                      >
                        {connecting === "Twitter" ? "Connecting..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="connector-settings__key-btn connector-settings__key-btn--cancel"
                        onClick={() => { setShowTwitterForm(false); setTwitterCredentials({ consumerKey: "", consumerSecret: "", accessToken: "", accessTokenSecret: "" }); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                {platform === "BlueBubbles" && showBlueBubblesForm && !connector && (
                  <form className="connector-settings__key-form" onSubmit={handleBlueBubblesSubmit}>
                    <input
                      ref={bbServerUrlRef}
                      className="connector-settings__key-input"
                      type="url"
                      placeholder="https://your-tunnel.example.com"
                      value={bbServerUrl}
                      onChange={(e) => setBbServerUrl(e.target.value)}
                      autoComplete="off"
                    />
                    <input
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="BlueBubbles Server password"
                      value={bbPassword}
                      onChange={(e) => setBbPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <p className="connector-settings__bb-disclosure">
                      RosterPilot will receive a copy of every iMessage and SMS sent or received
                      by this Mac while the connection is active, including conversations with
                      people who are not in your CRM. Messages from numbers we don't recognize
                      land in your Unmatched queue — they aren't shared with anyone else in your
                      organization until you assign them. You can disconnect anytime to stop
                      ingestion.
                    </p>
                    <label className="connector-settings__bb-consent">
                      <input
                        type="checkbox"
                        checked={bbConsentChecked}
                        onChange={(e) => setBbConsentChecked(e.target.checked)}
                      />
                      I understand and consent to ingesting my Mac's messages.
                    </label>
                    <a
                      className="connector-settings__bb-help"
                      href={BLUEBUBBLES_TUNNEL_DOC_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How do I expose my Mac with a tunnel?
                    </a>
                    {bbFormError && (
                      <div className="connector-settings__bb-form-error">{bbFormError}</div>
                    )}
                    <div className="connector-settings__key-form-actions">
                      <button
                        type="submit"
                        className="connector-settings__key-btn connector-settings__key-btn--save"
                        disabled={
                          connecting === "BlueBubbles" ||
                          !bbServerUrl.trim() ||
                          !bbPassword.trim() ||
                          !bbConsentChecked
                        }
                      >
                        {connecting === "BlueBubbles" ? blueBubblesButtonLabel : "Connect"}
                      </button>
                      <button
                        type="button"
                        className="connector-settings__key-btn connector-settings__key-btn--cancel"
                        onClick={resetBlueBubblesForm}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                {platform === "Quo" && showQuoKeyForm && !connector && (
                  <form className="connector-settings__key-form" onSubmit={handleQuoKeySubmit}>
                    <input
                      ref={quoInputRef}
                      className="connector-settings__key-input"
                      type="password"
                      placeholder="Paste your Quo API key"
                      value={quoKeyInput}
                      onChange={(e) => setQuoKeyInput(e.target.value)}
                    />
                    <div className="connector-settings__key-form-actions">
                      <button
                        type="submit"
                        className="connector-settings__key-btn connector-settings__key-btn--save"
                        disabled={connecting === "Quo" || !quoKeyInput.trim()}
                      >
                        {connecting === "Quo" ? "Connecting..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="connector-settings__key-btn connector-settings__key-btn--cancel"
                        onClick={() => { setShowQuoKeyForm(false); setQuoKeyInput(""); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
